import { Injectable } from '@angular/core';
import { MentorshipType } from '@app/mentoring/mentoring-api.model';
import { Mentorship } from '@app/mentoring/mentoring-api.model';
import {
  MentorshipStatusEnum,
  MentorshipTypeEnum,
} from '@app/mentoring/mentoring.enums';
import { MentoringApiService } from '@app/mentoring/services/mentoring-api.service';
import { MentoringModalsService } from '@app/mentoring/services/mentoring-modals.service';
import { MentoringTrackingService } from '@app/mentoring/services/mentoring-tracking.service';
import { FocusStackService } from '@app/shared/services/focus-stack.service';
import { Observable, of } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class MentoringTableMenuService {
  constructor(
    private focusStackService: FocusStackService,
    private mentoringApiService: MentoringApiService,
    private mentoringModalsService: MentoringModalsService,
    private mentoringTrackingService: MentoringTrackingService
  ) {}

  /**
   * Opens the mentorship accept modal
   *
   * @param mentorships - The current table data, used to update the table.
   * @param mentorship - The current mentorship to update.
   * @param mentorshipType - The mentorship type, currently always the mentee
   * @param element - The element to return focus to.
   */
  public acceptMentorship(
    mentorships: Mentorship[],
    mentorship: Mentorship,
    // In our use case, this will always be mentee,
    // but no reason to strip out versatility that
    // has already been added.
    mentorshipType: MentorshipType,
    element: HTMLElement
  ): Observable<Mentorship[]> {
    return this.mentoringModalsService.openAcceptMenteeModal(mentorship).pipe(
      finalize(() => {
        // when we are done with everything, return focus to the menu
        this.focusStackService.push(element);
      }),
      switchMap(({ updatedMentorship }) =>
        // update and return our table data
        of(this.updateTable(mentorships, updatedMentorship, mentorshipType))
      )
    );
  }

  /**
   * Opens the mentorship decline modal.
   *
   * @param mentorships - The current table data.
   * @param mentorship - The current mentorship to update
   * @param mentorshipType - The mentorship type, currently always the mentee
   * @param element - The element to return focus to.
   */
  public declineMentorship(
    mentorships: Mentorship[],
    mentorship: Mentorship,
    mentorshipType: MentorshipType,
    element: HTMLElement
  ): Observable<Mentorship[]> {
    return this.mentoringModalsService.openRejectMenteeModal(mentorship).pipe(
      finalize(() => {
        // when we are done with everything, return focus to the menu
        this.focusStackService.push(element);
      }),
      switchMap(({ updatedMentorship }) =>
        // update and return our table data
        of(this.updateTable(mentorships, updatedMentorship, mentorshipType))
      )
    );
  }

  /**
   * Opens the mentorship review overview modal
   *
   * @param mentorships - The current table data.
   * @param mentorship - The current mentorship to update
   * @param mentorshipType - The mentorship type
   * @param element - The element to return focus to
   */
  public reviewMentorship(
    mentorships: Mentorship[],
    mentorship: Mentorship,
    mentorshipType: MentorshipType,
    element: HTMLElement
  ): Observable<Mentorship[]> {
    return this.mentoringModalsService
      .openReviewMentorshipModal(mentorship, mentorshipType, element)
      .pipe(
        switchMap(() =>
          this.completeMentorship(
            mentorships,
            mentorship,
            mentorshipType,
            element
          )
        )
      );
  }

  /**
   * Open the more details modal
   *
   * @param mentorships - The mentorships in the table.
   * @param mentorship - The mentorship to show more details of
   * @param element - The element to return focus to.
   */
  public moreDetailsMentorship(
    mentorships: Mentorship[],
    mentorship: Mentorship,
    mentorshipType: MentorshipType,
    element: HTMLElement
  ): Observable<Mentorship[]> {
    return this.mentoringModalsService
      .openMoreDetailsMentorshipModal(mentorship, mentorshipType)
      .pipe(
        switchMap(({ updatedMentorship }) => {
          // update and return our table data
          return of(
            this.updateTable(
              mentorships,
              updatedMentorship,
              MentorshipTypeEnum.Mentee
            )
          );
        }),
        finalize(() => {
          // when we are done with everything, return focus to the menu
          this.focusStackService.push(element);
        })
      );
  }

  /**
   * Update a mentorships status to Complete
   * Possibly action from both the mentor and mentee
   *
   * @param mentorships all the mentorships in the current table
   * @param originalMentorship the current mentorship
   * @param mentorshipType Completion can be
   * @param element - The element to return focus to
   */
  public completeMentorship(
    mentorships: Mentorship[],
    originalMentorship: Mentorship,
    mentorshipType: MentorshipType,
    element: HTMLElement
  ): Observable<Mentorship[]> {
    const updatedMentorship = {
      ...originalMentorship,
      mentorshipStatusName: MentorshipStatusEnum.Completed,
    };

    return this.mentoringApiService
      .updateUserMentorships(updatedMentorship)
      .pipe(
        tap(() => {
          this.mentoringTrackingService.mentorshipCompleted(
            updatedMentorship,
            mentorshipType
          );
        }),
        finalize(() => {
          this.focusStackService.push(element);
        }),
        switchMap((updatedMentorship) =>
          // update and return our table data
          of(this.updateTable(mentorships, updatedMentorship, mentorshipType))
        )
      );
  }

  /**
   * Removed a mentorship from the UI by archiving it
   * Possibly action from both the mentor and mentee
   *
   * @param originalMentorship the current mentorship
   * @param mentorships all the mentorships in the current table
   * @param mentorshipType Completion can be
   * @param element - The element to return focus to
   */
  public removeMentorship(
    mentorships: Mentorship[],
    mentorship: Mentorship,
    mentorshipType: MentorshipType,
    element: HTMLElement // TODO: did we forget to add focus here?
  ): Observable<Mentorship[]> {
    // remove user mentorship(s)
    return this.mentoringApiService
      .archiveUserMentorships(
        mentorship.mentorUserProfileKey,
        mentorship.menteeUserProfileKey
      )
      .pipe(
        tap(() => {
          this.mentoringTrackingService.mentorshipArchived(
            mentorship,
            mentorshipType
          );
        }),
        switchMap(() =>
          // update and return our table data
          of(
            this.updateTable(
              mentorships,
              {
                ...mentorship,
                mentorshipStatusName: MentorshipStatusEnum.None,
              },
              mentorshipType
            )
          )
        )
      );
  }

  private updateTable(
    mentorships: Mentorship[],
    updatedMentorship: Mentorship,
    mentorshipRelation: MentorshipType
  ): Mentorship[] {
    // peel status off of our updated mentorship
    const { mentorshipStatusName: status } = updatedMentorship;
    // Remove a mentorship from the list
    if (status === MentorshipStatusEnum.None) {
      return mentorships.filter((mentorship) => {
        // Filter the mentorships that should still be displayed
        if (
          (mentorshipRelation === MentorshipTypeEnum.Mentee &&
            mentorship.menteeUserProfileKey !==
              updatedMentorship.menteeUserProfileKey) ||
          (mentorshipRelation === MentorshipTypeEnum.Mentor &&
            mentorship.mentorUserProfileKey !==
              updatedMentorship.mentorUserProfileKey)
        ) {
          return true;
        }
      });
    }
    // Else update mentorship and return the list
    return mentorships.map((mentorship) => {
      // Find the matching mentorship in the list and update the mentorship
      if (
        (mentorshipRelation === MentorshipTypeEnum.Mentee &&
          mentorship.menteeUserProfileKey ===
            updatedMentorship.menteeUserProfileKey) ||
        (mentorshipRelation === MentorshipTypeEnum.Mentor &&
          mentorship.mentorUserProfileKey ===
            updatedMentorship.mentorUserProfileKey)
      ) {
        mentorship = {
          ...mentorship,
          ...updatedMentorship,
        };
      }
      return mentorship;
    });
  }
}
